import React from "react";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { BadgeCheck, ShieldCheck, Youtube, Plane, ArrowRight, Phone } from "lucide-react";
import { Reveal, SectionHeading, SmartImage } from "../components/common";
import EnquiryForm from "../components/EnquiryForm";
import { droneImages, droneServices, business } from "../data/mock";

export default function Drone() {
  return (
    <div className="overflow-hidden">
      {/* HERO */}
      <section className="relative pt-32 pb-20 md:pt-44 md:pb-28">
        <div className="absolute inset-0 z-0">
          <SmartImage src={droneImages.hero} alt="Drone pilot" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-slate-900/40" />
        </div>
        <div className="max-w-7xl mx-auto px-5 relative z-10">
          <Reveal>
            <div className="inline-flex items-center gap-2 rounded-full bg-violet-500/20 border border-violet-300/30 backdrop-blur-md px-4 py-1.5 text-sm font-semibold text-violet-100">
              <BadgeCheck size={16} /> Commercial Drone License Holder
            </div>
            <h1 className="mt-5 font-heading text-4xl md:text-6xl font-extrabold text-white max-w-3xl leading-tight">
              Licensed aerial photography &amp; video from above.
            </h1>
            <p className="mt-5 text-lg text-slate-200 max-w-xl">
              Fred is a licensed commercial drone pilot capturing crisp aerial stills and cinematic 4K footage across Cobram, Barooga and the wider region — flown safely and legally.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#drone-enquiry" className="inline-flex items-center gap-2 rounded-full bg-violet-500 hover:bg-violet-600 text-white font-semibold px-6 py-3.5 shadow-lg shadow-violet-500/30 transition-all hover:-translate-y-0.5">Book a flight <ArrowRight size={16} /></a>
              <a href={business.youtube} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white font-semibold px-6 py-3.5 hover:bg-white/20 transition-all"><Youtube size={18} className="text-red-400" /> Watch showreel</a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* LICENSE HIGHLIGHT */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-5">
          <Reveal>
            <div className="rounded-3xl bg-gradient-to-br from-violet-50 to-indigo-50 border border-violet-100 p-8 md:p-10 grid md:grid-cols-3 gap-6 items-center">
              <div className="md:col-span-2">
                <div className="flex items-center gap-2 text-violet-600 font-semibold"><ShieldCheck size={20} /> Flown legally &amp; insured</div>
                <h2 className="mt-3 font-heading text-2xl md:text-3xl font-bold text-slate-900">Why a Commercial Drone License matters</h2>
                <p className="mt-3 text-slate-600">Hiring a licensed pilot means your job is flown to the rules — legally, safely and with the right approvals. That protects you, your property and everyone on the ground, while delivering professional-grade results you can actually use commercially.</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[["Licensed", "Commercial pilot"], ["Safe", "Rules-compliant flights"], ["4K", "Ultra-HD footage"], ["Local", "Knows the area"]].map(([a, b]) => (
                  <div key={a} className="rounded-2xl bg-white p-4 text-center shadow-sm"><div className="font-heading text-2xl font-bold text-violet-600">{a}</div><div className="text-xs text-slate-500 mt-1">{b}</div></div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-5">
          <SectionHeading eyebrow="Aerial Services" title="What Fred can capture for you." subtitle="From property and farmland to events and inspections — shot from the perfect angle." />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {droneServices.map((s, i) => {
              const Icon = Icons[s.icon] || Plane;
              return (
                <Reveal key={s.title} delay={(i % 3) * 0.06}>
                  <motion.div whileHover={{ y: -6 }} transition={{ type: "spring", stiffness: 300 }}
                    className="group h-full rounded-2xl bg-white border border-slate-200 p-6 hover:border-violet-200 hover:shadow-xl hover:shadow-violet-100/50 transition-all">
                    <span className="flex items-center justify-center w-12 h-12 rounded-xl bg-violet-50 text-violet-600 group-hover:bg-violet-500 group-hover:text-white transition-colors"><Icon size={24} /></span>
                    <h3 className="mt-5 font-heading font-bold text-lg text-slate-900">{s.title}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                  </motion.div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* GALLERY STRIP */}
      <section className="py-10">
        <div className="max-w-7xl mx-auto px-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[droneImages.sky, droneImages.farm1, droneImages.pilot, droneImages.farm2].map((src, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="overflow-hidden rounded-2xl group"><SmartImage src={src} alt="Aerial work" className="h-48 w-full object-cover transition-transform duration-500 group-hover:scale-110" /></div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* YOUTUBE SHOWCASE */}
      <section className="py-20 md:py-24 bg-slate-900">
        <div className="max-w-4xl mx-auto px-5 text-center">
          <SectionHeading light eyebrow="On YouTube" title="See the drone work in motion." subtitle="Fred shares his aerial and video work on his YouTube channel, MrFredMustica." />
          <Reveal delay={0.1} className="mt-10">
            <a href={business.youtube} target="_blank" rel="noreferrer" className="group block relative overflow-hidden rounded-3xl">
              <SmartImage src={droneImages.hero} alt="YouTube showcase" className="w-full h-72 md:h-96 object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-slate-900/50 flex items-center justify-center">
                <span className="flex items-center justify-center w-20 h-20 rounded-full bg-red-600 text-white shadow-2xl group-hover:scale-110 transition-transform"><Youtube size={40} /></span>
              </div>
            </a>
            <a href={business.youtube} target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-2 rounded-full bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-3 transition-colors"><Youtube size={18} /> Watch on YouTube</a>
          </Reveal>
        </div>
      </section>

      {/* ENQUIRY */}
      <section id="drone-enquiry" className="py-20 md:py-24 scroll-mt-20">
        <div className="max-w-3xl mx-auto px-5">
          <SectionHeading eyebrow="Book Fred" title="Request a drone job." subtitle="Tell Fred what you'd like captured and where — he'll get back with availability and a quote." />
          <div className="mt-10">
            <EnquiryForm
              type="drone"
              cta="Send Drone Enquiry"
              extraFields={[
                { name: "jobType", label: "Type of job", type: "select", options: ["Aerial Photography", "Cinematic Video", "Real Estate / Property", "Agriculture / Farmland", "Event", "Inspection", "Other"] },
                { name: "location", label: "Location", placeholder: "e.g. Barooga, NSW" },
              ]}
            />
          </div>
          <p className="mt-6 text-center text-slate-500">Prefer to chat first? <a href={business.phoneHref} className="inline-flex items-center gap-1 text-violet-600 font-semibold"><Phone size={15} /> {business.phone}</a></p>
        </div>
      </section>
    </div>
  );
}
